(() => {
  const namespace = window.KGGroutCalculator;

  if (!namespace) {
    return;
  }

  const getDrawerLocale = () => {
    const hostname = window.location.hostname.toLowerCase();
    const locale = String(
      window.Shopify?.locale || document.documentElement.lang || ''
    ).toLowerCase();

    if (locale.startsWith('da') || hostname.endsWith('.dk')) {
      return 'da';
    }

    if (
      locale.startsWith('no') ||
      locale.startsWith('nb') ||
      hostname.endsWith('.no')
    ) {
      return 'no';
    }

    return 'sv';
  };

  const drawerI18nFallbacks = {
    sv: {
      based_on_selected_area: 'Baseras på vald yta',
      recommended_quantity: 'Rekommenderad mängd',
      calculated_need: 'Beräknat behov',
      consumption: 'Åtgång',
      table_consumption: 'Åtgång enligt tabell',
      package_singular: 'förpackning',
      package_plural: 'förpackningar',
      package_size: 'Förpackningsstorlek',
      leveling_recommendation: 'Nivåsystemrekommendation',
      leveling_hat: 'Nivåhatt',
      leveling_band: 'Nivåband',
      leveling_screw: 'Nivåskruv',
      mounting_gun: 'Monteringspistol',
      one_to_one_leveling: '1:1 mot nivåsystemet',
      used_for_leveling_system: 'Används till nivåsystemet',
      per_project: 'Per projekt',
      estimated_leveling_bands: 'Beräknat antal nivåband',
      estimated_leveling_screws: 'Beräknat antal nivåskruv',
      estimated_spacers: 'Beräknat antal kryss',
      estimated_tile_spacers: 'Uppskattat antal kakelkryss',
      spacer_recommendation: 'Kakelkryssrekommendation',
      spacer_product: 'Kakelkryss',
      multifunction_spacer_product: 'Multifunktions kakelkryss',
      spacer_size_exact: 'Fogbredd styr storleken exakt',
      complementary_parts_one_to_one:
        'Kompletterande delar behövs normalt 1:1 mot nivåprodukten'
    },
    da: {
      based_on_selected_area: 'Baseret på valgt areal',
      recommended_quantity: 'Anbefalet mængde',
      calculated_need: 'Beregnet behov',
      consumption: 'Forbrug',
      table_consumption: 'Forbrug ifølge tabel',
      package_singular: 'pakke',
      package_plural: 'pakker',
      package_size: 'Pakkestørrelse',
      leveling_recommendation: 'Nivelleringssystemanbefaling',
      leveling_hat: 'Niveauhætte',
      leveling_band: 'Niveaubånd',
      leveling_screw: 'Niveauskrue',
      mounting_gun: 'Monteringspistol',
      one_to_one_leveling: '1:1 mod nivelleringssystemet',
      used_for_leveling_system: 'Bruges til nivelleringssystemet',
      per_project: 'Pr. projekt',
      estimated_leveling_bands: 'Beregnet antal niveaubånd',
      estimated_leveling_screws: 'Beregnet antal niveauskruer',
      estimated_spacers: 'Beregnet antal kryds',
      estimated_tile_spacers: 'Anslået antal flisekryds',
      spacer_recommendation: 'Flisekrydsanbefaling',
      spacer_product: 'Flisekryds',
      multifunction_spacer_product: 'Multifunktions flisekryds',
      spacer_size_exact: 'Fugebredden styrer størrelsen præcist',
      complementary_parts_one_to_one:
        'Supplerende dele behøves normalt 1:1 mod nivelleringsproduktet'
    },
    no: {
      based_on_selected_area: 'Basert på valgt areal',
      recommended_quantity: 'Anbefalt mengde',
      calculated_need: 'Beregnet behov',
      consumption: 'Forbruk',
      table_consumption: 'Forbruk ifølge tabell',
      package_singular: 'pakke',
      package_plural: 'pakker',
      package_size: 'Pakkestørrelse',
      leveling_recommendation: 'Nivåsystemanbefaling',
      leveling_hat: 'Nivåhette',
      leveling_band: 'Nivåbånd',
      leveling_screw: 'Nivåskrue',
      mounting_gun: 'Monteringspistol',
      one_to_one_leveling: '1:1 mot nivåsystemet',
      used_for_leveling_system: 'Brukes til nivåsystemet',
      per_project: 'Per prosjekt',
      estimated_leveling_bands: 'Beregnet antall nivåbånd',
      estimated_leveling_screws: 'Beregnet antall nivåskruer',
      estimated_spacers: 'Beregnet antall kryss',
      estimated_tile_spacers: 'Estimert antall fliskryss',
      spacer_recommendation: 'Fliskryssanbefaling',
      spacer_product: 'Fliskryss',
      multifunction_spacer_product: 'Multifunksjons fliskryss',
      spacer_size_exact: 'Fugebredden styrer størrelsen nøyaktig',
      complementary_parts_one_to_one:
        'Supplerende deler trengs normalt 1:1 mot nivåproduktet'
    }
  };

  const drawerI18n = (key, fallback) => {
    const locale = getDrawerLocale();
    const translated = window.KGDrawerI18n?.recommendedUpsells?.[key];
    const swedishFallback = drawerI18nFallbacks.sv[key] || fallback;

    if (
      translated &&
      (locale === 'sv' || translated !== swedishFallback)
    ) {
      return translated;
    }

    return drawerI18nFallbacks[locale]?.[key] || swedishFallback;
  };

  const packageUnit = (quantity) =>
    quantity === 1
      ? drawerI18n('package_singular', 'förpackning')
      : drawerI18n('package_plural', 'förpackningar');

  namespace.calculateGrout = (ctx) => {
    if (!ctx.elements.resultElement) {
      return;
    }

    const validationError = namespace.validateBaseData(ctx);

    if (validationError) {
      namespace.showError(ctx, validationError);
      return;
    }

    const {
      externalAreaInput,
      resultElement,
      selectedAreaOutput,
      drawerAreaOutput,
      addButton
    } =
      ctx.elements;
    const {
      tileDimensions,
      tileThicknessMm,
      jointWidthMm,
      packageWeightKg,
      groutTitle,
      groutCoefficient,
      groutVariantAvailable,
      groutVariantId,
      calculatorScope
    } = ctx.data;

    const parsedAreaFromInput = namespace.parseLocalizedNumber(
      externalAreaInput.value
    );
    const parsedAreaFromWindow = Number(window.userInputM2 || 0);
    const areaM2 =
      Number.isFinite(parsedAreaFromInput) && parsedAreaFromInput > 0
        ? parsedAreaFromInput
        : Number.isFinite(ctx.state.calculatedAreaM2) &&
            ctx.state.calculatedAreaM2 > 0
          ? ctx.state.calculatedAreaM2
          : Number.isFinite(parsedAreaFromWindow) &&
              parsedAreaFromWindow > 0
            ? parsedAreaFromWindow
            : 0;

    if (!Number.isFinite(areaM2) || areaM2 <= 0) {
      ctx.state.recommendedPackages = 0;
      ctx.state.calculatedAreaM2 = 0;

      resultElement.innerHTML =
        '<p>Ange en giltig yta större än 0 m².</p>';

      if (selectedAreaOutput) {
        selectedAreaOutput.textContent = '–';
      }

      if (drawerAreaOutput) {
        drawerAreaOutput.textContent = '–';
      }

      if (addButton) {
        addButton.disabled = true;
      }

      if (calculatorScope === 'fogmassa') {
        namespace.syncUpsellDrawerRecommendation(ctx, {
          metafield: 'fogmassa',
          variantId: groutVariantId,
          quantity: 1
        });
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    ctx.state.calculatedAreaM2 = areaM2;

    if (selectedAreaOutput) {
      selectedAreaOutput.textContent =
        `${namespace.formatNumber(areaM2)} m²`;
    }

    if (drawerAreaOutput) {
      drawerAreaOutput.textContent =
        `${namespace.formatNumber(areaM2)} m²`;
    }

    const consumptionKgPerM2 =
      (
        (
          tileDimensions.lengthMm +
          tileDimensions.widthMm
        ) /
        (
          tileDimensions.lengthMm *
          tileDimensions.widthMm
        )
      ) *
      tileThicknessMm *
      jointWidthMm *
      groutCoefficient;

    const requiredKg = consumptionKgPerM2 * areaM2;

    ctx.state.recommendedPackages = Math.max(
      1,
      Math.ceil(requiredKg / packageWeightKg)
    );

    const suppliedKg =
      ctx.state.recommendedPackages * packageWeightKg;

    const remainingKg = Math.max(0, suppliedKg - requiredKg);
    const selectedDrawerQty =
      namespace.getUpsellDrawerSelectedQuantity(ctx);
    const selectedDrawerKg =
      Number.isFinite(selectedDrawerQty) && selectedDrawerQty > 0
        ? selectedDrawerQty * packageWeightKg
        : 0;

    if (calculatorScope === 'fogmassa') {
      resultElement.innerHTML = `
        <div class="grout-calculator__drawer-summary">
          <div class="grout-calculator__drawer-summary-row">
            <span>${drawerI18n("based_on_selected_area", "Baseras på vald yta")}</span>
            <strong>${namespace.formatNumber(areaM2)} m²</strong>
          </div>
          <div class="grout-calculator__drawer-summary-row">
            <span>${drawerI18n("recommended_quantity", "Rekommenderad mängd")}</span>
            <strong>
              ${ctx.state.recommendedPackages}
              ${packageUnit(ctx.state.recommendedPackages)}
            </strong>
          </div>
          <div class="grout-calculator__drawer-summary-row">
            <span>${drawerI18n("calculated_need", "Beräknat behov")}</span>
            <strong>${namespace.formatNumber(requiredKg, 1)} kg</strong>
          </div>
          <div class="grout-calculator__drawer-summary-row">
            <span>${drawerI18n("consumption", "Åtgång")}</span>
            <strong>${namespace.formatNumber(consumptionKgPerM2, 3)} kg/m²</strong>
          </div>
        </div>
      `;
    } else {
      resultElement.innerHTML = `
        <p class="grout-calculator__recommendation">
          Vi rekommenderar
          <strong>
            ${ctx.state.recommendedPackages}
            ${packageUnit(ctx.state.recommendedPackages)}
          </strong>
          av ${groutTitle}.
        </p>

        <p class="grout-calculator__calculation">
          ${drawerI18n("calculated_need_colon", "Beräknat behov:")}
          <strong>
            ${namespace.formatNumber(requiredKg, 1)} kg
          </strong>
        </p>

        <p class="grout-calculator__calculation">
          Total mängd:
          <strong>
            ${namespace.formatNumber(suppliedKg)} kg
          </strong>
        </p>

        <p class="grout-calculator__calculation">
          Beräknad marginal:
          <strong>
            ${namespace.formatNumber(remainingKg)} kg
          </strong>
        </p>

        <p class="grout-calculator__consumption">
          Beräknad åtgång:
          ${namespace.formatNumber(consumptionKgPerM2, 3)} kg/m².
        </p>
      `;
    }

    if (calculatorScope === 'fogmassa') {
      namespace.syncUpsellDrawerRecommendation(ctx, {
        metafield: 'fogmassa',
        variantId: groutVariantId,
        quantity: ctx.state.recommendedPackages
      });
    }

    if (addButton) {
      addButton.disabled =
        ctx.state.recommendedPackages < 1 ||
        !groutVariantAvailable;
    }

    namespace.refreshBundleUI(ctx);
  };

  namespace.calculateUnderlay = (ctx) => {
    const {
      underlayResultElement,
      externalAreaInput,
      addUnderlayButton
    } = ctx.elements;
    const {
      underlayCoverageM2,
      underlayTitle,
      underlayVariantId,
      underlayVariantAvailable
    } = ctx.data;

    if (!underlayResultElement || !externalAreaInput) {
      return;
    }

    const parsedAreaFromInput = namespace.parseLocalizedNumber(
      externalAreaInput.value
    );
    const parsedAreaFromWindow = Number(window.userInputM2 || 0);
    const areaM2 =
      Number.isFinite(parsedAreaFromInput) && parsedAreaFromInput > 0
        ? parsedAreaFromInput
        : Number.isFinite(ctx.state.calculatedAreaM2) &&
            ctx.state.calculatedAreaM2 > 0
          ? ctx.state.calculatedAreaM2
          : Number.isFinite(parsedAreaFromWindow) &&
              parsedAreaFromWindow > 0
            ? parsedAreaFromWindow
            : 0;

    if (
      !Number.isFinite(areaM2) ||
      areaM2 <= 0 ||
      !Number.isFinite(underlayCoverageM2) ||
      underlayCoverageM2 <= 0
    ) {
      ctx.state.calculatedAreaM2 = 0;
      ctx.state.recommendedUnderlayUnits = 0;
      underlayResultElement.innerHTML =
        '<p>Vi räknar ut golvunderlag när du väljer antal m² ovan.</p>';

      if (addUnderlayButton) {
        addUnderlayButton.disabled = true;
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    ctx.state.calculatedAreaM2 = areaM2;
    ctx.state.recommendedUnderlayUnits = Math.max(
      1,
      Math.ceil(areaM2 / underlayCoverageM2)
    );

    const coveredArea =
      ctx.state.recommendedUnderlayUnits *
      underlayCoverageM2;
    const selectedDrawerQty =
      namespace.getUpsellDrawerSelectedQuantity(ctx);
    const selectedCoveredArea =
      Number.isFinite(selectedDrawerQty) && selectedDrawerQty > 0
        ? selectedDrawerQty * underlayCoverageM2
        : 0;

    underlayResultElement.innerHTML = `
      <p class="grout-calculator__calculation">
        ${drawerI18n("based_on_selected_area_colon", "Baseras på vald yta:")}
        <strong>${namespace.formatNumber(areaM2)} m²</strong>
      </p>
      <p class="grout-calculator__recommendation">
        Vi rekommenderar
        <strong>
          ${ctx.state.recommendedUnderlayUnits}
          ${
            ctx.state.recommendedUnderlayUnits === 1
              ? 'rulle'
              : 'rullar'
          }
        </strong>
        av ${underlayTitle}.
      </p>
      <p class="grout-calculator__calculation">
        Täckning per rulle:
        <strong>${namespace.formatNumber(underlayCoverageM2, 1)} m²</strong>
      </p>
      <p class="grout-calculator__calculation">
        Total täckning:
        <strong>${namespace.formatNumber(coveredArea, 1)} m²</strong>
      </p>
    `;

    if (addUnderlayButton) {
      addUnderlayButton.disabled =
        ctx.state.recommendedUnderlayUnits < 1 ||
        !Number.isFinite(underlayVariantId) ||
        underlayVariantId <= 0 ||
        !underlayVariantAvailable;
    }

    if (ctx.data.calculatorScope === 'golvunderlag') {
      namespace.syncUpsellDrawerRecommendation(ctx, {
        metafield: 'golvunderlag',
        variantId: underlayVariantId,
        quantity: ctx.state.recommendedUnderlayUnits
      });
    }

    namespace.refreshBundleUI(ctx);
  };

  namespace.calculatePrimer = (ctx) => {
    const {
      primerResultElement,
      externalAreaInput,
      addPrimerButton
    } = ctx.elements;
    const {
      primerTitle,
      primerVariantId,
      primerVariantAvailable,
      primerPackageUnits
    } = ctx.data;

    if (!primerResultElement || !externalAreaInput) {
      return;
    }

    const parsedAreaFromInput = namespace.parseLocalizedNumber(
      externalAreaInput.value
    );
    const parsedAreaFromWindow = Number(window.userInputM2 || 0);
    const areaM2 =
      Number.isFinite(parsedAreaFromInput) && parsedAreaFromInput > 0
        ? parsedAreaFromInput
        : Number.isFinite(ctx.state.calculatedAreaM2) &&
            ctx.state.calculatedAreaM2 > 0
          ? ctx.state.calculatedAreaM2
          : Number.isFinite(parsedAreaFromWindow) &&
              parsedAreaFromWindow > 0
            ? parsedAreaFromWindow
            : 0;

    if (
      !Number.isFinite(areaM2) ||
      areaM2 <= 0 ||
      !Number.isFinite(primerPackageUnits) ||
      primerPackageUnits <= 0
    ) {
      ctx.state.recommendedPrimerUnits = 0;
      primerResultElement.innerHTML =
        '<p>Vi räknar ut primer när du väljer antal m² ovan.</p>';

      if (addPrimerButton) {
        addPrimerButton.disabled = true;
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    ctx.state.calculatedAreaM2 = areaM2;

    const primerProfile =
      namespace.resolvePrimerConsumptionProfile(
        primerTitle
      );
    const consumptionMinPerM2 = Number(
      primerProfile?.min || 0
    );
    const consumptionMaxPerM2 = Number(
      primerProfile?.max || 0
    );
    const primerUnit = primerProfile?.unit || 'kg';

    const requiredPrimerMin =
      areaM2 * consumptionMinPerM2;
    const requiredPrimerMax =
      areaM2 * consumptionMaxPerM2;

    ctx.state.recommendedPrimerUnits = Math.max(
      1,
      Math.ceil(requiredPrimerMax / primerPackageUnits)
    );

    const totalPrimerUnits =
      ctx.state.recommendedPrimerUnits *
      primerPackageUnits;
    const isUpsellsDrawer =
      namespace.isUpsellsDrawerCalculator(ctx);
    const selectedDrawerQty =
      namespace.getUpsellDrawerSelectedQuantity(ctx);
    const selectedPrimerUnits =
      Number.isFinite(selectedDrawerQty) && selectedDrawerQty > 0
        ? selectedDrawerQty * primerPackageUnits
        : 0;

    const formattedConsumption =
      consumptionMinPerM2 === consumptionMaxPerM2
        ? `${namespace.formatNumber(
            consumptionMaxPerM2,
            2
          )} ${primerUnit}/m²`
        : `${namespace.formatNumber(
            consumptionMinPerM2,
            2
          )}–${namespace.formatNumber(
            consumptionMaxPerM2,
            2
          )} ${primerUnit}/m²`;

    const formattedNeed =
      requiredPrimerMin === requiredPrimerMax
        ? `${namespace.formatNumber(
            requiredPrimerMax,
            1
          )} ${primerUnit}`
        : `${namespace.formatNumber(
            requiredPrimerMin,
            1
          )}–${namespace.formatNumber(
            requiredPrimerMax,
            1
          )} ${primerUnit}`;

    primerResultElement.innerHTML = isUpsellsDrawer
      ? `
      <div class="grout-calculator__drawer-summary">
        <div class="grout-calculator__drawer-summary-row">
          <span>${drawerI18n("based_on_selected_area", "Baseras på vald yta")}</span>
          <strong>${namespace.formatNumber(areaM2)} m²</strong>
        </div>
        <div class="grout-calculator__drawer-summary-row">
          <span>${drawerI18n("recommended_quantity", "Rekommenderad mängd")}</span>
          <strong>${ctx.state.recommendedPrimerUnits} ${packageUnit(ctx.state.recommendedPrimerUnits)}</strong>
        </div>
        <div class="grout-calculator__drawer-summary-row">
          <span>${drawerI18n("calculated_need", "Beräknat behov")}</span>
          <strong>${formattedNeed}</strong>
        </div>
        <div class="grout-calculator__drawer-summary-row">
          <span>${drawerI18n("consumption", "Åtgång")}</span>
          <strong>${formattedConsumption}</strong>
        </div>
      </div>
    `
      : `
      <p class="grout-calculator__recommendation">
        Vi rekommenderar
        <strong>
          ${ctx.state.recommendedPrimerUnits}
          ${packageUnit(ctx.state.recommendedPrimerUnits)}
        </strong>
        av ${primerTitle}.
      </p>
      <p class="grout-calculator__calculation">
        ${drawerI18n("based_on_selected_area_colon", "Baseras på vald yta:")}
        <strong>${namespace.formatNumber(areaM2)} m²</strong>
      </p>
      <p class="grout-calculator__calculation">
        ${drawerI18n("consumption_colon", "Åtgång:")}
        <strong>${formattedConsumption}</strong>
      </p>
      <p class="grout-calculator__calculation">
        ${drawerI18n("calculated_need_colon", "Beräknat behov:")}
        <strong>${formattedNeed}</strong>
      </p>
      <p class="grout-calculator__calculation">
        Total mängd:
        <strong>${namespace.formatNumber(totalPrimerUnits, 1)} ${primerUnit}</strong>
      </p>
    `;

    if (addPrimerButton) {
      addPrimerButton.disabled =
        ctx.state.recommendedPrimerUnits < 1 ||
        !Number.isFinite(primerVariantId) ||
        primerVariantId <= 0 ||
        !primerVariantAvailable;
    }

    if (ctx.data.calculatorScope === 'primer') {
      namespace.syncUpsellDrawerRecommendation(ctx, {
        metafield: 'primer',
        variantId: primerVariantId,
        quantity: ctx.state.recommendedPrimerUnits
      });
    }

    namespace.refreshBundleUI(ctx);
  };

  namespace.calculatePiedestal = (ctx) => {
    const {
      piedestalResultElement,
      externalAreaInput,
      addPiedestalButton
    } = ctx.elements;
    const {
      tileDimensions,
      piedestalTitle,
      piedestalVariantId,
      piedestalVariantAvailable,
      piedestalPackUnits
    } = ctx.data;
    if (!piedestalResultElement || !externalAreaInput) {
      return;
    }

    const parsedAreaFromInput = namespace.parseLocalizedNumber(
      externalAreaInput.value
    );
    const parsedAreaFromWindow = Number(window.userInputM2 || 0);
    const areaM2 =
      Number.isFinite(parsedAreaFromInput) && parsedAreaFromInput > 0
        ? parsedAreaFromInput
        : Number.isFinite(ctx.state.calculatedAreaM2) &&
            ctx.state.calculatedAreaM2 > 0
          ? ctx.state.calculatedAreaM2
          : Number.isFinite(parsedAreaFromWindow) &&
              parsedAreaFromWindow > 0
            ? parsedAreaFromWindow
            : 0;

    if (!Number.isFinite(areaM2) || areaM2 <= 0) {
      ctx.state.recommendedPiedestalUnits = 0;
      piedestalResultElement.innerHTML =
        '<p>Vi räknar ut piedestaler när du väljer antal m² ovan.</p>';

      if (addPiedestalButton) {
        addPiedestalButton.disabled = true;
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    ctx.state.calculatedAreaM2 = areaM2;

    ctx.state.calculatedAreaM2 = areaM2;

    if (!tileDimensions) {
      ctx.state.recommendedPiedestalUnits = 0;
      piedestalResultElement.innerHTML =
        '<p>Plattans mått kunde inte tolkas för piedestalberäkningen.</p>';

      if (addPiedestalButton) {
        addPiedestalButton.disabled = true;
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    if (
      !Number.isFinite(piedestalPackUnits) ||
      piedestalPackUnits <= 0
    ) {
      ctx.state.recommendedPiedestalUnits = 0;
      piedestalResultElement.innerHTML =
        '<p>Förpackningsstorleken för piedestalen kunde inte tolkas.</p>';

      if (addPiedestalButton) {
        addPiedestalButton.disabled = true;
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    ctx.state.calculatedAreaM2 = areaM2;

    const pedestalsPerM2 = 6;

    const estimatedPedestals =
      pedestalsPerM2 * areaM2;

    ctx.state.recommendedPiedestalUnits = Math.max(
      1,
      Math.ceil(estimatedPedestals / piedestalPackUnits)
    );

    const totalPedestals =
      ctx.state.recommendedPiedestalUnits *
      piedestalPackUnits;
    const isUpsellsDrawer =
      namespace.isUpsellsDrawerCalculator(ctx);
    const selectedDrawerQty =
      namespace.getUpsellDrawerSelectedQuantity(ctx);
    const selectedPedestals =
      Number.isFinite(selectedDrawerQty) && selectedDrawerQty > 0
        ? selectedDrawerQty * piedestalPackUnits
        : 0;

    piedestalResultElement.innerHTML = isUpsellsDrawer
      ? `
      <div class="grout-calculator__drawer-summary">
        <div class="grout-calculator__drawer-summary-row">
          <span>${drawerI18n("based_on_selected_area", "Baseras på vald yta")}</span>
          <strong>${namespace.formatNumber(areaM2)} m²</strong>
        </div>
      <div class="grout-calculator__drawer-summary-row">
          <span>${drawerI18n("recommended_quantity", "Rekommenderad mängd")}</span>
          <strong>${ctx.state.recommendedPiedestalUnits} st</strong>
        </div>
        <div class="grout-calculator__drawer-summary-row">
          <span>${drawerI18n("calculated_need", "Beräknat behov")}</span>
          <strong>${namespace.formatNumber(estimatedPedestals, 1)} st</strong>
        </div>
        <div class="grout-calculator__drawer-summary-row">
          <span>${drawerI18n("consumption", "Åtgång")}</span>
          <strong>${namespace.formatNumber(pedestalsPerM2, 2)} st/m²</strong>
        </div>
      </div>
    `
      : `
      <p class="grout-calculator__recommendation">
        Vi rekommenderar
        <strong>
          ${ctx.state.recommendedPiedestalUnits}
          st
        </strong>
        av ${piedestalTitle}.
      </p>
      <p class="grout-calculator__calculation">
        ${drawerI18n("based_on_selected_area_colon", "Baseras på vald yta:")}
        <strong>${namespace.formatNumber(areaM2)} m²</strong>
      </p>
      <p class="grout-calculator__calculation">
        Beräknad åtgång:
        <strong>${namespace.formatNumber(pedestalsPerM2, 2)} st/m²</strong>
      </p>
      <p class="grout-calculator__calculation">
        Uppskattat behov:
        <strong>${namespace.formatNumber(estimatedPedestals, 1)} st</strong>
      </p>
      <p class="grout-calculator__calculation">
        Total mängd:
        <strong>${namespace.formatNumber(totalPedestals, 0)} st</strong>
      </p>
    `;

    if (addPiedestalButton) {
      addPiedestalButton.disabled =
        ctx.state.recommendedPiedestalUnits < 1 ||
        !Number.isFinite(piedestalVariantId) ||
        piedestalVariantId <= 0 ||
        !piedestalVariantAvailable;
    }

    if (ctx.data.calculatorScope === 'piedestal') {
      namespace.syncUpsellDrawerRecommendation(ctx, {
        metafield: 'piedestal',
        variantId: piedestalVariantId,
        quantity: ctx.state.recommendedPiedestalUnits
      });
    }

    namespace.refreshBundleUI(ctx);
  };

  namespace.calculateFastmassa = (ctx) => {
    const {
      fastmassaResultElement,
      externalAreaInput,
      addFastmassaButton
    } = ctx.elements;
    const {
      tileDimensions,
      fastmassaTitle,
      fastmassaVariantId,
      fastmassaVariantAvailable,
      fastmassaPackageKg
    } = ctx.data;
    const { FASTMASSA_MARGIN_FACTOR } =
      namespace.constants;

    if (!fastmassaResultElement || !externalAreaInput) {
      return;
    }

    const parsedAreaFromInput = namespace.parseLocalizedNumber(
      externalAreaInput.value
    );
    const parsedAreaFromWindow = Number(window.userInputM2 || 0);
    const areaM2 =
      Number.isFinite(parsedAreaFromInput) && parsedAreaFromInput > 0
        ? parsedAreaFromInput
        : Number.isFinite(ctx.state.calculatedAreaM2) &&
            ctx.state.calculatedAreaM2 > 0
          ? ctx.state.calculatedAreaM2
          : Number.isFinite(parsedAreaFromWindow) &&
              parsedAreaFromWindow > 0
            ? parsedAreaFromWindow
            : 0;

    if (!Number.isFinite(areaM2) || areaM2 <= 0) {
      ctx.state.recommendedFastmassaUnits = 0;
      fastmassaResultElement.innerHTML =
        '<p>Vi räknar ut fästmassa när du väljer antal m² ovan.</p>';

      if (addFastmassaButton) {
        addFastmassaButton.disabled = true;
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    ctx.state.calculatedAreaM2 = areaM2;

    ctx.state.calculatedAreaM2 = areaM2;

    if (!tileDimensions) {
      ctx.state.recommendedFastmassaUnits = 0;
      fastmassaResultElement.innerHTML =
        '<p>Plattans mått kunde inte tolkas för fästmassaberäkningen.</p>';

      if (addFastmassaButton) {
        addFastmassaButton.disabled = true;
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    if (
      !Number.isFinite(fastmassaPackageKg) ||
      fastmassaPackageKg <= 0
    ) {
      ctx.state.recommendedFastmassaUnits = 0;
      fastmassaResultElement.innerHTML =
        '<p>Säckstorleken för fästmassan kunde inte tolkas.</p>';

      if (addFastmassaButton) {
        addFastmassaButton.disabled = true;
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    ctx.state.calculatedAreaM2 = areaM2;

    const consumptionKgPerM2 =
      namespace.resolveFastmassaConsumptionKgPerM2(
        {
          ...tileDimensions,
          title: fastmassaTitle
        }
      );

    const requiredKg =
      areaM2 *
      consumptionKgPerM2 *
      FASTMASSA_MARGIN_FACTOR;

    ctx.state.recommendedFastmassaUnits = Math.max(
      1,
      Math.ceil(requiredKg / fastmassaPackageKg)
    );

    const totalKg =
      ctx.state.recommendedFastmassaUnits *
      fastmassaPackageKg;
    const isUpsellsDrawer =
      namespace.isUpsellsDrawerCalculator(ctx);
    const selectedDrawerQty =
      namespace.getUpsellDrawerSelectedQuantity(ctx);
    const selectedTotalKg =
      Number.isFinite(selectedDrawerQty) && selectedDrawerQty > 0
        ? selectedDrawerQty * fastmassaPackageKg
        : 0;

    fastmassaResultElement.innerHTML = isUpsellsDrawer
      ? `
      <div class="grout-calculator__drawer-summary">
        <div class="grout-calculator__drawer-summary-row">
          <span>${drawerI18n("based_on_selected_area", "Baseras på vald yta")}</span>
          <strong>${namespace.formatNumber(areaM2)} m²</strong>
        </div>
        <div class="grout-calculator__drawer-summary-row">
          <span>${drawerI18n("recommended_quantity", "Rekommenderad mängd")}</span>
          <strong>${ctx.state.recommendedFastmassaUnits} ${
            ctx.state.recommendedFastmassaUnits === 1 ? 'säck' : 'säckar'
          }</strong>
        </div>
        <div class="grout-calculator__drawer-summary-row">
          <span>${drawerI18n("calculated_need", "Beräknat behov")}</span>
          <strong>${namespace.formatNumber(requiredKg, 1)} kg</strong>
        </div>
        <div class="grout-calculator__drawer-summary-row">
          <span>${drawerI18n("consumption", "Åtgång")}</span>
          <strong>${namespace.formatNumber(consumptionKgPerM2, 1)} kg/m²</strong>
        </div>
      </div>
    `
      : `
      <p class="grout-calculator__calculation">
        ${drawerI18n("based_on_selected_area_colon", "Baseras på vald yta:")}
        <strong>${namespace.formatNumber(areaM2)} m²</strong>
      </p>
      <p class="grout-calculator__calculation">
        Beräknad åtgång:
        <strong>${namespace.formatNumber(consumptionKgPerM2, 1)} kg/m²</strong>
      </p>
      <p class="grout-calculator__calculation">
        ${drawerI18n("need_including_margin", "Beräknat behov inkl. marginal")}:
        <strong>${namespace.formatNumber(requiredKg, 1)} kg</strong>
      </p>
      <p class="grout-calculator__calculation">
        ${drawerI18n("recommended_quantity_colon", "Rekommenderad mängd:")}
        <strong>
          ${ctx.state.recommendedFastmassaUnits}
          ${
            ctx.state.recommendedFastmassaUnits === 1
              ? 'säck'
              : 'säckar'
          }
        </strong>
      </p>
      <p class="grout-calculator__calculation">
        Total mängd:
        <strong>${namespace.formatNumber(totalKg, 0)} kg</strong>
      </p>
    `;

    if (addFastmassaButton) {
      addFastmassaButton.disabled =
        ctx.state.recommendedFastmassaUnits < 1 ||
        !Number.isFinite(fastmassaVariantId) ||
        fastmassaVariantId <= 0 ||
        !fastmassaVariantAvailable;
    }

    if (ctx.data.calculatorScope === 'fastmassa') {
      namespace.syncUpsellDrawerRecommendation(ctx, {
        metafield: 'fastmassa',
        variantId: fastmassaVariantId,
        quantity: ctx.state.recommendedFastmassaUnits
      });
    }

    namespace.refreshBundleUI(ctx);
  };

  namespace.calculatePassbitar = (ctx) => {
    const {
      passbitarResultElement,
      externalAreaInput,
      addPassbitarButton
    } = ctx.elements;
    const {
      tileDimensions,
      passbitarPackUnits
    } = ctx.data;
    const { selectedPassbitarProduct } = ctx.state;

    if (!passbitarResultElement || !externalAreaInput) {
      return;
    }

    if (!selectedPassbitarProduct) {
      ctx.state.recommendedPassbitarUnits = 0;
      passbitarResultElement.innerHTML =
        '<p>Ingen passbit är kopplad till produkten.</p>';

      if (addPassbitarButton) {
        addPassbitarButton.disabled = true;
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    const parsedAreaFromInput = namespace.parseLocalizedNumber(
      externalAreaInput.value
    );
    const parsedAreaFromWindow = Number(window.userInputM2 || 0);
    const areaM2 =
      Number.isFinite(parsedAreaFromInput) && parsedAreaFromInput > 0
        ? parsedAreaFromInput
        : Number.isFinite(ctx.state.calculatedAreaM2) &&
            ctx.state.calculatedAreaM2 > 0
          ? ctx.state.calculatedAreaM2
          : Number.isFinite(parsedAreaFromWindow) &&
              parsedAreaFromWindow > 0
            ? parsedAreaFromWindow
            : 0;

    if (!Number.isFinite(areaM2) || areaM2 <= 0) {
      ctx.state.recommendedPassbitarUnits = 0;
      passbitarResultElement.innerHTML =
        '<p>Vi räknar ut passbitar när du väljer antal m² ovan.</p>';

      if (addPassbitarButton) {
        addPassbitarButton.disabled = true;
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    if (!tileDimensions) {
      ctx.state.recommendedPassbitarUnits = 0;
      passbitarResultElement.innerHTML =
        '<p>Plattans mått kunde inte tolkas för passbitsberäkningen.</p>';

      if (addPassbitarButton) {
        addPassbitarButton.disabled = true;
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    if (
      !Number.isFinite(passbitarPackUnits) ||
      passbitarPackUnits <= 0
    ) {
      ctx.state.recommendedPassbitarUnits = 0;
      passbitarResultElement.innerHTML =
        '<p>Förpackningsstorleken för passbitarna kunde inte tolkas.</p>';

      if (addPassbitarButton) {
        addPassbitarButton.disabled = true;
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    const pcsPerM2 =
      namespace.resolvePassbitarPcsPerM2(tileDimensions);

    if (!Number.isFinite(pcsPerM2) || pcsPerM2 <= 0) {
      ctx.state.recommendedPassbitarUnits = 0;
      passbitarResultElement.innerHTML =
        '<p>Ingen passbitsregel finns ännu för detta plattformat.</p>';

      if (addPassbitarButton) {
        addPassbitarButton.disabled = true;
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    ctx.state.calculatedAreaM2 = areaM2;

    const estimatedPassbitar = pcsPerM2 * areaM2;

    ctx.state.recommendedPassbitarUnits = Math.max(
      1,
      Math.ceil(estimatedPassbitar)
    );
    const isUpsellsDrawer =
      namespace.isUpsellsDrawerCalculator(ctx);
    const selectedDrawerQty =
      namespace.getUpsellDrawerSelectedQuantity(ctx);

    passbitarResultElement.innerHTML = isUpsellsDrawer
      ? `
      <div class="grout-calculator__drawer-summary">
        <div class="grout-calculator__drawer-summary-row">
          <span>${drawerI18n("based_on_selected_area", "Baseras på vald yta")}</span>
          <strong>${namespace.formatNumber(areaM2)} m²</strong>
        </div>
        <div class="grout-calculator__drawer-summary-row">
          <span>${drawerI18n("recommended_quantity", "Rekommenderad mängd")}</span>
          <strong>${namespace.formatNumber(estimatedPassbitar, 0)} st</strong>
        </div>
        <div class="grout-calculator__drawer-summary-row">
          <span>${drawerI18n("consumption", "Åtgång")}</span>
          <strong>${namespace.formatNumber(pcsPerM2, 0)} st/m²</strong>
        </div>
      </div>
    `
      : `
      <p class="grout-calculator__recommendation">
        Vi rekommenderar
        <strong>
          ${namespace.formatNumber(estimatedPassbitar, 0)} st
        </strong>
        i ${selectedPassbitarProduct.title}.
      </p>
      <p class="grout-calculator__calculation">
        ${drawerI18n("based_on_selected_area_colon", "Baseras på vald yta:")}
        <strong>${namespace.formatNumber(areaM2)} m²</strong>
      </p>
      <p class="grout-calculator__calculation">
        ${drawerI18n("consumption_colon", "Åtgång:")}
        <strong>${namespace.formatNumber(pcsPerM2, 0)} st/m²</strong>
      </p>
      <p class="grout-calculator__calculation">
        Uppskattat behov:
        <strong>${namespace.formatNumber(estimatedPassbitar, 0)} st</strong>
      </p>
    `;

    if (addPassbitarButton) {
      addPassbitarButton.disabled =
        ctx.state.recommendedPassbitarUnits < 1 ||
        !Number.isFinite(selectedPassbitarProduct.variantId) ||
        selectedPassbitarProduct.variantId <= 0 ||
        !selectedPassbitarProduct.available;
    }

    if (ctx.data.calculatorScope === 'passbitar') {
      namespace.syncUpsellDrawerRecommendation(ctx, {
        metafield: 'passbitar',
        variantId: selectedPassbitarProduct.variantId,
        quantity: ctx.state.recommendedPassbitarUnits
      });
    }

    namespace.refreshBundleUI(ctx);
  };

  namespace.calculateSilicone = (ctx) => {
    const {
      siliconeResultElement,
      externalAreaInput,
      siliconeScenarioSelect,
      siliconeManualMetersInput,
      siliconeJointWidthInput,
      siliconeJointDepthInput,
      siliconeShapeSelect,
      addSiliconeButton
    } = ctx.elements;
    const {
      siliconeTitle,
      siliconeVolumeMl,
      siliconeVariantId,
      siliconeVariantAvailable
    } = ctx.data;

    if (!siliconeResultElement || !externalAreaInput) {
      return;
    }

    const areaM2 = namespace.parseLocalizedNumber(
      externalAreaInput.value
    );

    const scenarioFactor = namespace.parseLocalizedNumber(
      siliconeScenarioSelect?.value
    );

    const manualMeters = namespace.parseLocalizedNumber(
      siliconeManualMetersInput?.value
    );

    const siliconeJointWidthMm =
      namespace.parseLocalizedNumber(
        siliconeJointWidthInput?.value
      );

    const siliconeJointDepthMm =
      namespace.parseLocalizedNumber(
        siliconeJointDepthInput?.value
      );

    const resolvedScenarioFactor =
      Number.isFinite(scenarioFactor) && scenarioFactor > 0
        ? scenarioFactor
        : 1.2;
    const resolvedJointWidthMm =
      Number.isFinite(siliconeJointWidthMm) &&
      siliconeJointWidthMm > 0
        ? siliconeJointWidthMm
        : 5;
    const resolvedJointDepthMm =
      Number.isFinite(siliconeJointDepthMm) &&
      siliconeJointDepthMm > 0
        ? siliconeJointDepthMm
        : 5;
    const shapeFactor =
      siliconeShapeSelect?.value === 'rectangle' ? 1 : 0.5;

    if (!Number.isFinite(areaM2) || areaM2 <= 0) {
      ctx.state.recommendedSiliconeUnits = 0;
      siliconeResultElement.innerHTML =
        '<p>Ange en giltig yta för att uppskatta silikonbehovet.</p>';

      if (addSiliconeButton) {
        addSiliconeButton.disabled = true;
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    const estimatedMeters =
      Number.isFinite(manualMeters) && manualMeters > 0
        ? manualMeters
        : areaM2 * resolvedScenarioFactor;

    const mlPerMeter =
      resolvedJointWidthMm *
      resolvedJointDepthMm *
      shapeFactor;

    const adjustedConsumptionMl =
      estimatedMeters *
      mlPerMeter *
      namespace.constants.SILICONE_MARGIN_FACTOR;

    ctx.state.recommendedSiliconeUnits = Math.max(
      1,
      Math.ceil(
        adjustedConsumptionMl / siliconeVolumeMl
      )
    );
    const isUpsellsDrawer =
      namespace.isUpsellsDrawerCalculator(ctx);
    const selectedDrawerQty =
      namespace.getUpsellDrawerSelectedQuantity(ctx);
    const selectedTotalMl =
      Number.isFinite(selectedDrawerQty) && selectedDrawerQty > 0
        ? selectedDrawerQty * siliconeVolumeMl
        : 0;

    siliconeResultElement.innerHTML =
      isUpsellsDrawer || ctx.data.calculatorScope === 'silikon'
      ? `
      <div class="grout-calculator__drawer-summary">
        <div class="grout-calculator__drawer-summary-row">
          <span>${drawerI18n("based_on_selected_area", "Baseras på vald yta")}</span>
          <strong>${namespace.formatNumber(areaM2)} m²</strong>
        </div>
        <div class="grout-calculator__drawer-summary-row">
          <span>${drawerI18n("recommended_quantity", "Rekommenderad mängd")}</span>
          <strong>${ctx.state.recommendedSiliconeUnits} ${
            ctx.state.recommendedSiliconeUnits === 1 ? 'patron' : 'patroner'
          }</strong>
        </div>
        <div class="grout-calculator__drawer-summary-row">
          <span>Uppskattad foglängd</span>
          <strong>${namespace.formatNumber(estimatedMeters, 1)} m</strong>
        </div>
        <div class="grout-calculator__drawer-summary-row">
          <span>Behov inkl. marginal</span>
          <strong>${namespace.formatNumber(adjustedConsumptionMl, 0)} ml</strong>
        </div>
        <div class="grout-calculator__drawer-summary-row">
          <span>Patronstorlek</span>
          <strong>${namespace.formatNumber(siliconeVolumeMl, 0)} ml</strong>
        </div>
      </div>
    `
      : `
      <p class="silicone-calculator__recommendation">
        Vi rekommenderar
        <strong>
          ${ctx.state.recommendedSiliconeUnits}
          ${
            ctx.state.recommendedSiliconeUnits === 1
              ? 'patron'
              : 'patroner'
          }
        </strong>
        av ${siliconeTitle}.
      </p>
      <p class="grout-calculator__calculation">
        ${drawerI18n("based_on_selected_area_colon", "Baseras på vald yta:")}
        <strong>${namespace.formatNumber(areaM2)} m²</strong>
      </p>
      <div class="silicone-calculator__stats">
        <div class="silicone-calculator__stat">
          <span class="silicone-calculator__stat-label">Uppskattad foglängd</span>
          <strong class="silicone-calculator__stat-value">${namespace.formatNumber(estimatedMeters, 1)} m</strong>
        </div>
        <div class="silicone-calculator__stat">
          <span class="silicone-calculator__stat-label">Behov inkl. 15 % marginal</span>
          <strong class="silicone-calculator__stat-value">${namespace.formatNumber(adjustedConsumptionMl, 0)} ml</strong>
        </div>
        <div class="silicone-calculator__stat">
          <span class="silicone-calculator__stat-label">Patronstorlek</span>
          <strong class="silicone-calculator__stat-value">${namespace.formatNumber(siliconeVolumeMl, 0)} ml</strong>
        </div>
      </div>
    `;

    if (addSiliconeButton) {
      addSiliconeButton.disabled =
        ctx.state.recommendedSiliconeUnits < 1 ||
        !Number.isFinite(siliconeVariantId) ||
        siliconeVariantId <= 0 ||
        !siliconeVariantAvailable;
    }

    if (ctx.data.calculatorScope === 'silikon') {
      namespace.syncUpsellDrawerRecommendation(ctx, {
        metafield: 'silikon',
        variantId: siliconeVariantId,
        quantity: ctx.state.recommendedSiliconeUnits
      });
    }

    namespace.refreshBundleUI(ctx);
  };

  namespace.calculateLeveling = (ctx) => {
    const {
      levelingResultElement,
      externalAreaInput,
      addLevelingButton,
      addLevelingHatButton,
      levelingHatResultElement,
      levelingGunResultElement
    } = ctx.elements;
    const {
      tileDimensions,
      levelingHat,
      levelingGun
    } = ctx.data;
    const { selectedLevelingProduct } = ctx.state;

    if (!levelingResultElement || !externalAreaInput) {
      return;
    }

    if (!selectedLevelingProduct) {
      ctx.state.recommendedLevelingUnits = 0;
      ctx.state.recommendedLevelingHatUnits = 0;
      ctx.state.recommendedLevelingGunUnits = 0;
      levelingResultElement.innerHTML =
        '<p>Ingen nivelleringsprodukt är kopplad till produkten.</p>';

      if (addLevelingButton) {
        addLevelingButton.disabled = true;
      }

      if (addLevelingHatButton) {
        addLevelingHatButton.disabled = true;
      }

      if (levelingHatResultElement) {
        levelingHatResultElement.innerHTML = '';
      }

      if (levelingGunResultElement) {
        levelingGunResultElement.innerHTML = '';
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    const parsedAreaFromInput = namespace.parseLocalizedNumber(
      externalAreaInput.value
    );
    const parsedAreaFromWindow = Number(window.userInputM2 || 0);
    const areaM2 =
      Number.isFinite(parsedAreaFromInput) && parsedAreaFromInput > 0
        ? parsedAreaFromInput
        : Number.isFinite(ctx.state.calculatedAreaM2) &&
            ctx.state.calculatedAreaM2 > 0
          ? ctx.state.calculatedAreaM2
          : Number.isFinite(parsedAreaFromWindow) &&
              parsedAreaFromWindow > 0
            ? parsedAreaFromWindow
            : 0;

    if (!Number.isFinite(areaM2) || areaM2 <= 0) {
      ctx.state.recommendedLevelingUnits = 0;
      ctx.state.recommendedLevelingHatUnits = 0;
      ctx.state.recommendedLevelingGunUnits = 0;
      levelingResultElement.innerHTML =
        '<p>Ange en giltig yta för att uppskatta nivåsystemet.</p>';

      if (addLevelingButton) {
        addLevelingButton.disabled = true;
      }

      if (addLevelingHatButton) {
        addLevelingHatButton.disabled = true;
      }

      if (levelingHatResultElement) {
        levelingHatResultElement.innerHTML = '';
      }

      if (levelingGunResultElement) {
        levelingGunResultElement.innerHTML = '';
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    ctx.state.calculatedAreaM2 = areaM2;

    if (!tileDimensions) {
      ctx.state.recommendedLevelingUnits = 0;
      ctx.state.recommendedLevelingHatUnits = 0;
      ctx.state.recommendedLevelingGunUnits = 0;
      levelingResultElement.innerHTML =
        '<p>Plattans mått kunde inte tolkas för nivelleringsberäkningen.</p>';

      if (addLevelingButton) {
        addLevelingButton.disabled = true;
      }

      if (addLevelingHatButton) {
        addLevelingHatButton.disabled = true;
      }

      if (levelingHatResultElement) {
        levelingHatResultElement.innerHTML = '';
      }

      if (levelingGunResultElement) {
        levelingGunResultElement.innerHTML = '';
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    const lengthM = tileDimensions.lengthCm / 100;
    const widthM = tileDimensions.widthCm / 100;
    const selectedLevelingKind =
      String(selectedLevelingProduct.kind || 'skruv');
    const activeDrawerProductKind = String(
      ctx.state.activeUpsellDrawerProduct?.kind || selectedLevelingKind
    );
    const isLevelingBand =
      selectedLevelingKind === 'band';
    const isMainLevelingSystem =
      selectedLevelingKind === 'band' ||
      selectedLevelingKind === 'skruv';

    if (!isMainLevelingSystem) {
      ctx.state.recommendedLevelingUnits = 0;
      ctx.state.recommendedLevelingHatUnits = 0;
      ctx.state.recommendedLevelingGunUnits = 0;
      levelingResultElement.innerHTML =
        '<p>Välj ett nivåsystem för att se rekommendationen.</p>';

      if (addLevelingButton) {
        addLevelingButton.disabled = true;
      }

      if (addLevelingHatButton) {
        addLevelingHatButton.disabled = true;
      }

      if (levelingHatResultElement) {
        levelingHatResultElement.innerHTML = '';
      }

      if (levelingGunResultElement) {
        levelingGunResultElement.innerHTML = '';
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    let estimatedClips = 0;
    let clipSpacingMm = null;
    let pcsPerM2 = null;

    if (isLevelingBand) {
      pcsPerM2 = namespace.resolveEasyLevelBandPcsPerM2(
        tileDimensions.lengthCm,
        tileDimensions.widthCm
      );

      if (!Number.isFinite(pcsPerM2) || pcsPerM2 <= 0) {
        ctx.state.recommendedLevelingUnits = 0;
        ctx.state.recommendedLevelingHatUnits = 0;
        levelingResultElement.innerHTML =
          '<p>Plattans mått stöds inte av nivåbandstabellen ännu.</p>';

        if (addLevelingButton) {
          addLevelingButton.disabled = true;
        }

        if (addLevelingHatButton) {
          addLevelingHatButton.disabled = true;
        }

        if (levelingHatResultElement) {
          levelingHatResultElement.innerHTML = '';
        }

        if (levelingGunResultElement) {
          levelingGunResultElement.innerHTML = '';
        }

        namespace.refreshBundleUI(ctx);
        return;
      }

      estimatedClips = pcsPerM2 * areaM2;
    } else {
      clipSpacingMm =
        Math.max(
          tileDimensions.lengthMm,
          tileDimensions.widthMm
        ) >= 900
          ? namespace.constants.LEVELING_LARGE_FORMAT_SPACING_MM
          : namespace.constants.LEVELING_STANDARD_SPACING_MM;

      const clipsPerM2 =
        ((1 / lengthM) + (1 / widthM)) /
        (clipSpacingMm / 1000);

      estimatedClips =
        clipsPerM2 *
        areaM2 *
        namespace.constants.LEVELING_MARGIN_FACTOR;
    }

    ctx.state.recommendedLevelingUnits = Math.max(
      1,
      Math.ceil(
        estimatedClips / selectedLevelingProduct.packUnits
      )
    );
    const selectedDrawerQty =
      namespace.getUpsellDrawerSelectedQuantity(ctx, {
        metafield: 'nivaskruv',
        variantId: selectedLevelingProduct.variantId
      });
    const selectedLevelingTotal =
      Number.isFinite(selectedDrawerQty) && selectedDrawerQty > 0
        ? selectedDrawerQty * selectedLevelingProduct.packUnits
        : 0;

    if (levelingHat && levelingHat.packUnits > 0) {
      ctx.state.recommendedLevelingHatUnits = Math.max(
        1,
        Math.ceil(estimatedClips / levelingHat.packUnits)
      );
    } else {
      ctx.state.recommendedLevelingHatUnits = 0;
    }

    if (levelingGun) {
      ctx.state.recommendedLevelingGunUnits = 1;
    } else {
      ctx.state.recommendedLevelingGunUnits = 0;
    }

    if (ctx.data.calculatorScope === 'nivaskruv') {
      if (activeDrawerProductKind === 'hat' && levelingHat) {
        levelingResultElement.innerHTML = `
        <div class="leveling-calculator__drawer-summary">
          <div class="leveling-calculator__drawer-heading">
            <span class="leveling-calculator__drawer-kicker">${drawerI18n("leveling_recommendation", "Nivåsystemrekommendation")}</span>
            <strong>${drawerI18n("leveling_hat", "Nivåhatt")}</strong>
          </div>
          <div class="grout-calculator__drawer-summary">
            <div class="grout-calculator__drawer-summary-row">
              <span>${drawerI18n("based_on_selected_area", "Baseras på vald yta")}</span>
              <strong>${namespace.formatNumber(areaM2)} m²</strong>
            </div>
            <div class="grout-calculator__drawer-summary-row">
              <span>${drawerI18n("recommended_quantity", "Rekommenderad mängd")}</span>
              <strong>${ctx.state.recommendedLevelingHatUnits} ${
                packageUnit(ctx.state.recommendedLevelingHatUnits)
              }</strong>
            </div>
            <div class="grout-calculator__drawer-summary-row">
              <span>${drawerI18n("one_to_one_leveling", "1:1 mot nivåsystemet")}</span>
              <strong>${namespace.formatNumber(estimatedClips, 0)} st</strong>
            </div>
            <div class="grout-calculator__drawer-summary-row">
              <span>${drawerI18n("package_size", "Förpackningsstorlek")}</span>
              <strong>${namespace.formatNumber(levelingHat.packUnits, 0)} st</strong>
            </div>
          </div>
        </div>
      `;
      } else if (
        (activeDrawerProductKind === 'gun' || activeDrawerProductKind === 'pistol') &&
        levelingGun
      ) {
        levelingResultElement.innerHTML = `
        <div class="leveling-calculator__drawer-summary">
          <div class="leveling-calculator__drawer-heading">
            <span class="leveling-calculator__drawer-kicker">${drawerI18n("leveling_recommendation", "Nivåsystemrekommendation")}</span>
            <strong>${drawerI18n("mounting_gun", "Monteringspistol")}</strong>
          </div>
          <div class="grout-calculator__drawer-summary">
            <div class="grout-calculator__drawer-summary-row">
              <span>${drawerI18n("based_on_selected_area", "Baseras på vald yta")}</span>
              <strong>${namespace.formatNumber(areaM2)} m²</strong>
            </div>
            <div class="grout-calculator__drawer-summary-row">
              <span>${drawerI18n("recommended_quantity", "Rekommenderad mängd")}</span>
              <strong>1 st</strong>
            </div>
            <div class="grout-calculator__drawer-summary-row">
              <span>${drawerI18n("used_for_leveling_system", "Används till nivåsystemet")}</span>
              <strong>${drawerI18n("per_project", "Per projekt")}</strong>
            </div>
          </div>
        </div>
      `;
      } else {
      levelingResultElement.innerHTML = `
        <div class="leveling-calculator__drawer-summary">
          <div class="leveling-calculator__drawer-heading">
            <span class="leveling-calculator__drawer-kicker">${drawerI18n("leveling_recommendation", "Nivåsystemrekommendation")}</span>
            <strong>${selectedLevelingProduct.kind === 'band' ? drawerI18n("leveling_band", "Nivåband") : drawerI18n("leveling_screw", "Nivåskruv")} ${selectedLevelingProduct.size.replace('.', ',')} mm</strong>
          </div>
          <div class="grout-calculator__drawer-summary">
            <div class="grout-calculator__drawer-summary-row">
              <span>${drawerI18n("based_on_selected_area", "Baseras på vald yta")}</span>
              <strong>${namespace.formatNumber(areaM2)} m²</strong>
            </div>
            <div class="grout-calculator__drawer-summary-row">
              <span>${drawerI18n("recommended_quantity", "Rekommenderad mängd")}</span>
              <strong>${ctx.state.recommendedLevelingUnits} ${
                packageUnit(ctx.state.recommendedLevelingUnits)
              }</strong>
            </div>
            <div class="grout-calculator__drawer-summary-row">
              <span>${isLevelingBand ? drawerI18n("estimated_leveling_bands", "Beräknat antal nivåband") : drawerI18n("estimated_leveling_screws", "Beräknat antal nivåskruv")}</span>
              <strong>${namespace.formatNumber(estimatedClips, 0)} st</strong>
            </div>
            <div class="grout-calculator__drawer-summary-row">
              <span>${drawerI18n("package_size", "Förpackningsstorlek")}</span>
              <strong>${namespace.formatNumber(selectedLevelingProduct.packUnits, 0)} st</strong>
            </div>
            ${
              isLevelingBand
                ? `
            <div class="grout-calculator__drawer-summary-row">
              <span>${drawerI18n("table_consumption", "Åtgång enligt tabell")}</span>
              <strong>${namespace.formatNumber(pcsPerM2, 0)} st/m²</strong>
            </div>
            `
                : ''
            }
          </div>
        </div>
      `;
      }
    } else if (namespace.isUpsellsDrawerCalculator(ctx)) {
      levelingResultElement.innerHTML = `
        <div class="grout-calculator__drawer-summary">
          <div class="grout-calculator__drawer-summary-row">
            <span>${drawerI18n("based_on_selected_area", "Baseras på vald yta")}</span>
            <strong>${namespace.formatNumber(areaM2)} m²</strong>
          </div>
          <div class="grout-calculator__drawer-summary-row">
            <span>${drawerI18n("recommended_quantity", "Rekommenderad mängd")}</span>
            <strong>${ctx.state.recommendedLevelingUnits} ${
              packageUnit(ctx.state.recommendedLevelingUnits)
            }</strong>
          </div>
          <div class="grout-calculator__drawer-summary-row">
            <span>${isLevelingBand ? drawerI18n("estimated_leveling_bands", "Beräknat antal nivåband") : drawerI18n("estimated_leveling_screws", "Beräknat antal nivåskruv")}</span>
            <strong>${namespace.formatNumber(estimatedClips, 0)} st</strong>
          </div>
          <div class="grout-calculator__drawer-summary-row">
            <span>${drawerI18n("package_size", "Förpackningsstorlek")}</span>
            <strong>${namespace.formatNumber(selectedLevelingProduct.packUnits, 0)} st</strong>
          </div>
        </div>
      `;
    } else {
      levelingResultElement.innerHTML = `
        <p class="leveling-calculator__recommendation">
          Vi rekommenderar
          <strong>
            ${ctx.state.recommendedLevelingUnits}
            ${packageUnit(ctx.state.recommendedLevelingUnits)}
          </strong>
          av ${selectedLevelingProduct.title}.
        </p>
        <div class="leveling-calculator__stats">
          <div class="leveling-calculator__stat">
            <span class="leveling-calculator__stat-label">${
              isLevelingBand
                ? drawerI18n("estimated_leveling_bands", "Uppskattat antal nivåband")
                : drawerI18n("estimated_leveling_screws", "Uppskattat antal nivåskruv")
            }</span>
            <strong class="leveling-calculator__stat-value">${namespace.formatNumber(estimatedClips, 0)} st</strong>
          </div>
          <div class="leveling-calculator__stat">
            <span class="leveling-calculator__stat-label">${drawerI18n("package_size", "Förpackningsstorlek")}</span>
            <strong class="leveling-calculator__stat-value">${namespace.formatNumber(selectedLevelingProduct.packUnits, 0)} st</strong>
          </div>
          ${
            isLevelingBand
              ? `
          <div class="leveling-calculator__stat">
            <span class="leveling-calculator__stat-label">${drawerI18n("table_consumption", "Åtgång enligt tabell")}</span>
            <strong class="leveling-calculator__stat-value">${namespace.formatNumber(pcsPerM2, 0)} st/m²</strong>
          </div>
          `
              : ''
          }
        </div>
      `;
    }

    if (levelingHatResultElement) {
      if (levelingHat) {
        levelingHatResultElement.dataset.recommendedUnits = String(
          ctx.state.recommendedLevelingHatUnits
        );
        levelingHatResultElement.dataset.requiredUnits = String(
          estimatedClips
        );
        levelingHatResultElement.dataset.packUnits = String(
          levelingHat.packUnits
        );

        if (ctx.data.calculatorScope !== 'nivaskruv') {
          levelingHatResultElement.innerHTML = `
            <p class="leveling-calculator__recommendation">
              Vi rekommenderar
              <strong>
                ${ctx.state.recommendedLevelingHatUnits}
                ${packageUnit(ctx.state.recommendedLevelingHatUnits)}
              </strong>
              av ${levelingHat.title}.
            </p>
            <div class="leveling-calculator__stats">
              <div class="leveling-calculator__stat">
                <span class="leveling-calculator__stat-label">${drawerI18n("complementary_parts_one_to_one", "Kompletterande delar behövs normalt 1:1 mot nivåprodukten")}</span>
                <strong class="leveling-calculator__stat-value">${namespace.formatNumber(estimatedClips, 0)} st</strong>
              </div>
              <div class="leveling-calculator__stat">
                <span class="leveling-calculator__stat-label">${drawerI18n("package_size", "Förpackningsstorlek")}</span>
                <strong class="leveling-calculator__stat-value">${namespace.formatNumber(levelingHat.packUnits, 0)} st</strong>
              </div>
            </div>
          `;
        } else {
          levelingHatResultElement.innerHTML = '';
        }
      } else {
        delete levelingHatResultElement.dataset.recommendedUnits;
        delete levelingHatResultElement.dataset.requiredUnits;
        delete levelingHatResultElement.dataset.packUnits;
        levelingHatResultElement.innerHTML =
          '<p>Ingen nivåhatt är kopplad till produkten.</p>';
      }
    }

    if (levelingGunResultElement) {
      if (levelingGun) {
        if (ctx.data.calculatorScope !== 'nivaskruv') {
          levelingGunResultElement.innerHTML =
            '<p>Vi rekommenderar 1 st monteringspistol till nivåsystemet.</p>';
        } else {
          levelingGunResultElement.innerHTML = '';
        }
      } else {
        levelingGunResultElement.innerHTML = '';
      }
    }

    if (addLevelingButton) {
      addLevelingButton.disabled =
        ctx.state.recommendedLevelingUnits < 1 ||
        !Number.isFinite(selectedLevelingProduct.variantId) ||
        selectedLevelingProduct.variantId <= 0 ||
        !selectedLevelingProduct.available;
    }

    if (addLevelingHatButton) {
      addLevelingHatButton.disabled =
        ctx.state.recommendedLevelingHatUnits < 1 ||
        !levelingHat ||
        !Number.isFinite(levelingHat.variantId) ||
        levelingHat.variantId <= 0 ||
        !levelingHat.available;
    }

    if (ctx.elements.addLevelingGunButton) {
      ctx.elements.addLevelingGunButton.disabled =
        ctx.state.recommendedLevelingGunUnits < 1 ||
        !levelingGun ||
        !Number.isFinite(levelingGun.variantId) ||
        levelingGun.variantId <= 0 ||
        !levelingGun.available;
    }

    if (ctx.data.calculatorScope === 'nivaskruv') {
      namespace.syncUpsellDrawerRecommendation(ctx, {
        metafield: 'nivaskruv',
        variantId: selectedLevelingProduct.variantId,
        quantity: ctx.state.recommendedLevelingUnits,
        setRecommended: true,
        setActive: false
      });

      if (levelingHat?.variantId && ctx.state.recommendedLevelingHatUnits > 0) {
        namespace.syncUpsellDrawerRecommendation(ctx, {
          metafield: 'nivaskruv',
          variantId: levelingHat.variantId,
          quantity: ctx.state.recommendedLevelingHatUnits,
          setRecommended: false,
          setActive: false
        });
      }

      if (levelingGun?.variantId && ctx.state.recommendedLevelingGunUnits > 0) {
        namespace.syncUpsellDrawerRecommendation(ctx, {
          metafield: 'nivaskruv',
          variantId: levelingGun.variantId,
          quantity: ctx.state.recommendedLevelingGunUnits,
          setRecommended: false,
          setActive: false
        });
      }
    }

    namespace.refreshBundleUI(ctx);
  };

  namespace.calculateSpacers = (ctx) => {
    const {
      spacerResultElement,
      externalAreaInput,
      addSpacerButton
    } = ctx.elements;
    const { tileDimensions } = ctx.data;
    const { selectedSpacerProduct } = ctx.state;

    if (!spacerResultElement || !externalAreaInput) {
      return;
    }

    if (!selectedSpacerProduct) {
      ctx.state.recommendedSpacerUnits = 0;
      spacerResultElement.innerHTML =
        '<p>Inga kakelkryss är kopplade till produkten.</p>';

      if (addSpacerButton) {
        addSpacerButton.disabled = true;
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    const parsedAreaFromInput = namespace.parseLocalizedNumber(
      externalAreaInput.value
    );
    const parsedAreaFromWindow = Number(window.userInputM2 || 0);
    const areaM2 =
      Number.isFinite(parsedAreaFromInput) && parsedAreaFromInput > 0
        ? parsedAreaFromInput
        : Number.isFinite(ctx.state.calculatedAreaM2) &&
            ctx.state.calculatedAreaM2 > 0
          ? ctx.state.calculatedAreaM2
          : Number.isFinite(parsedAreaFromWindow) &&
              parsedAreaFromWindow > 0
            ? parsedAreaFromWindow
            : 0;

    if (!Number.isFinite(areaM2) || areaM2 <= 0) {
      ctx.state.recommendedSpacerUnits = 0;
      spacerResultElement.innerHTML =
        '<p>Ange en giltig yta för att uppskatta kakelkryss.</p>';

      if (addSpacerButton) {
        addSpacerButton.disabled = true;
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    ctx.state.calculatedAreaM2 = areaM2;

    if (!tileDimensions) {
      ctx.state.recommendedSpacerUnits = 0;
      spacerResultElement.innerHTML =
        '<p>Plattans mått kunde inte tolkas för kakelkryssberäkningen.</p>';

      if (addSpacerButton) {
        addSpacerButton.disabled = true;
      }

      namespace.refreshBundleUI(ctx);
      return;
    }

    const lengthM = tileDimensions.lengthCm / 100;
    const widthM = tileDimensions.widthCm / 100;

    const estimatedSpacers =
      ((2 / lengthM) + (2 / widthM)) *
      areaM2 *
      namespace.constants.SPACER_MARGIN_FACTOR;

    ctx.state.recommendedSpacerUnits = Math.max(
      1,
      Math.ceil(
        estimatedSpacers / selectedSpacerProduct.packUnits
      )
    );
    const selectedDrawerQty =
      namespace.getUpsellDrawerSelectedQuantity(ctx, {
        metafield: 'nivaskruv',
        variantId: selectedSpacerProduct.variantId
      });
    const selectedSpacerTotal =
      Number.isFinite(selectedDrawerQty) &&
      selectedDrawerQty > 0
        ? selectedDrawerQty *
          selectedSpacerProduct.packUnits
        : 0;
    const spacerSizeLabel = String(selectedSpacerProduct.size || '')
      .replace('.', ',');
    const spacerTitle = String(selectedSpacerProduct.title || '').toLowerCase();
    const spacerProductLabel = `${
      spacerTitle.includes('multifunktion')
        ? drawerI18n("multifunction_spacer_product", "Multifunktions kakelkryss")
        : drawerI18n("spacer_product", "Kakelkryss")
    }${spacerSizeLabel ? ` ${spacerSizeLabel} mm` : ''}`;

    spacerResultElement.innerHTML =
      namespace.isUpsellsDrawerCalculator(ctx) ||
      ctx.data.calculatorScope === 'nivaskruv'
        ? `
      <div class="leveling-calculator__drawer-summary">
        <div class="leveling-calculator__drawer-heading">
          <span class="leveling-calculator__drawer-kicker">${drawerI18n("spacer_recommendation", "Kakelkryssrekommendation")}</span>
          <strong>${spacerProductLabel}</strong>
        </div>
        <div class="grout-calculator__drawer-summary">
          <div class="grout-calculator__drawer-summary-row">
            <span>${drawerI18n("based_on_selected_area", "Baseras på vald yta")}</span>
            <strong>${namespace.formatNumber(areaM2)} m²</strong>
          </div>
          <div class="grout-calculator__drawer-summary-row">
            <span>${drawerI18n("recommended_quantity", "Rekommenderad mängd")}</span>
            <strong>${ctx.state.recommendedSpacerUnits} ${
              packageUnit(ctx.state.recommendedSpacerUnits)
            }</strong>
          </div>
          <div class="grout-calculator__drawer-summary-row">
            <span>${drawerI18n("estimated_spacers", "Beräknat antal kryss")}</span>
            <strong>${namespace.formatNumber(estimatedSpacers, 0)} st</strong>
          </div>
          <div class="grout-calculator__drawer-summary-row">
            <span>${drawerI18n("consumption", "Åtgång")}</span>
            <strong>${namespace.formatNumber(estimatedSpacers / areaM2, 0)} st/m²</strong>
          </div>
        </div>
      </div>
    `
        : `
      <p class="spacer-calculator__recommendation">
        Vi rekommenderar
        <strong>
          ${ctx.state.recommendedSpacerUnits}
          ${packageUnit(ctx.state.recommendedSpacerUnits)}
        </strong>
        av ${selectedSpacerProduct.title}.
      </p>
      <div class="spacer-calculator__stats">
        <div class="spacer-calculator__stat">
          <span class="spacer-calculator__stat-label">${drawerI18n("spacer_size_exact", "Fogbredd styr storleken exakt")}</span>
          <strong class="spacer-calculator__stat-value">${selectedSpacerProduct.size.replace('.', ',')} mm</strong>
        </div>
        <div class="spacer-calculator__stat">
          <span class="spacer-calculator__stat-label">${drawerI18n("estimated_tile_spacers", "Uppskattat antal kakelkryss")}</span>
          <strong class="spacer-calculator__stat-value">${namespace.formatNumber(estimatedSpacers, 0)} st</strong>
        </div>
        <div class="spacer-calculator__stat">
          <span class="spacer-calculator__stat-label">${drawerI18n("package_size", "Förpackningsstorlek")}</span>
          <strong class="spacer-calculator__stat-value">${namespace.formatNumber(selectedSpacerProduct.packUnits, 0)} st</strong>
        </div>
      </div>
    `;

    if (addSpacerButton) {
      addSpacerButton.disabled =
        ctx.state.recommendedSpacerUnits < 1 ||
        !Number.isFinite(selectedSpacerProduct.variantId) ||
        selectedSpacerProduct.variantId <= 0 ||
        !selectedSpacerProduct.available;
    }

    if (ctx.data.calculatorScope === 'nivaskruv') {
      namespace.syncUpsellDrawerRecommendation(ctx, {
        metafield: 'nivaskruv',
        variantId: selectedSpacerProduct.variantId,
        quantity: ctx.state.recommendedSpacerUnits,
        setRecommended: true,
        setActive: false
      });
    }

    namespace.refreshBundleUI(ctx);
  };
})();
